import { useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'
import isEqual from 'lodash/isEqual'
import {
  generateAddressObject,
  generateContactsObject,
} from 'apps/fleet-planner/pages/loads/AddNewFacilityForm'
import { INewFacilityRequest } from 'apps/fleet-planner/schema/FacilitySchema'
import { ICustomerContact } from 'apps/fleet-planner/types'
import { updateFacilityThunk } from 'apps/fleet-planner/utils/actions/facilities'
import * as Api from 'apps/fleet-planner/utils/api'
import {
  IFacility,
  IFacilityAddress,
  IGetFacilitiesParams,
  IUpdateFacilityRequest,
} from 'apps/fleet-planner/utils/api/facilities'

/**
 * Hook for fetching/storing facility data and state
 * @see https://fp-staging.transfix.io/api-docs/index.html
 */
export const useFacilityApi = () => {
  const [isLoading, setLoading] = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  const [facilitiesList, setFacilitiesList] = useState<IFacility[]>([])
  const [error, setError] = useState<string | undefined>()
  const resetError = useCallback(() => setError(''), [])

  const dispatch = useDispatch()

  /**
   * Creates a new facility
   * @param {INewFacilityRequest} facility - Created facility object
   */
  const createNewFacility = useCallback(async (facility: INewFacilityRequest): Promise<
    IFacility | undefined
  > => {
    setLoading(true)
    try {
      const res = await Api.createNewFacility({
        facility,
      })
      setError(undefined)
      setLoading(false)
      return res?.facility
    } catch (e) {
      setLoading(false)
      setError(e)
      return undefined
    }
  }, [])

  /**
   * Updates an existing facility
   * @param {string} facilityId
   * @param {Partial<INewFacilityRequest>} changes - Any updates for this object
   */
  const updateFacility = useCallback(async (request: IUpdateFacilityRequest): Promise<
    IFacility | undefined
  > => {
    setLoading(true)
    try {
      const res = await Api.updateFacility(request.facility.id, request)
      setError(undefined)
      setLoading(false)
      return res?.facility
    } catch (e) {
      setLoading(false)
      setError(e)
      return undefined
    }
  }, [])

  /**
   * Copied from updateFacilityHandler() in <AddNewFacilityForm />
   * This function detects whether there are changes in the prev/new
   * objects and then triggers creation/update methods accordingly
   */
  const updateFacilityInfo = useCallback(
    async (
      facilities: IFacility[] = [],
      facilityId: string,
      address: Partial<IFacilityAddress> = {},
      contact: Partial<ICustomerContact> = {},
      newAddress: Partial<IFacilityAddress> = {},
      newContact: Partial<ICustomerContact> = {},
      addFacilityCallback: (facility: IFacility) => void
    ) => {
      if (!facilityId) return undefined
      const matchingFacility = facilities.filter(f => f.id === facilityId)[0] || {}
      const reduxUpdate: any = {}

      // Update address
      const nextAddress = generateAddressObject(newAddress)
      const prevAddress = generateAddressObject(address)
      const { addresses = [] } = matchingFacility || {}
      const { id: addressId } = addresses[0] || {}
      if (!isEqual(nextAddress, prevAddress)) {
        reduxUpdate.address = {
          ...nextAddress,
          id: addressId,
        }
      }

      // Update contacts
      const nextContact = generateContactsObject({
        name: newContact?.name ?? '',
        email: newContact?.email ?? '',
        phone: newContact?.phone ?? '',
        phoneExt: newContact?.phoneExt ?? '',
      })

      const prevContact = generateContactsObject({
        name: contact.name ?? '',
        email: contact.email ?? '',
        phone: contact.phone ?? '',
        phoneExt: contact.phoneExt ?? '',
      })

      const { contacts = [] } = matchingFacility || {}
      const { id: contactId } = contacts[0] || {}

      // Only update if object is new, has an id, and has a valid name
      if (
        !isEqual(nextContact, prevContact) &&
        !!(nextContact.name || nextContact.email || nextContact.phone)
      ) {
        reduxUpdate.contact = {
          ...nextContact,
          id: contactId,
        }
      }

      // Update facility
      if (newAddress.addressee !== address.addressee) {
        reduxUpdate.facility = {
          name: newAddress.addressee,
          id: facilityId,
        }
      }

      dispatch(
        updateFacilityThunk(facilityId, addressId, `${contactId}`, {
          ...reduxUpdate,
        })
      )

      let facilityUpdate: IFacility = {} as IFacility

      if (reduxUpdate?.facility) {
        facilityUpdate = reduxUpdate?.facility
      }

      if (reduxUpdate?.address) {
        facilityUpdate.addresses = [reduxUpdate?.address]
      }

      if (reduxUpdate?.contact) {
        facilityUpdate.contacts = [reduxUpdate?.contact]
      }

      return addFacilityCallback({
        ...matchingFacility,
        ...facilityUpdate,
      })
    },
    [dispatch]
  )

  /**
   * Fetch facilities data + return the facilities list
   */
  const getFacilities = useCallback(async (params: IGetFacilitiesParams): Promise<
    IFacility[] | undefined
  > => {
    setError('')
    setLoading(true)
    try {
      const {
        facilities: response,
        totalCount: totalFacilitiesCount,
      } = await Api.getRecentFacilities(params)
      setFacilitiesList(response)
      setTotalCount(totalFacilitiesCount)
      setLoading(false)
      return response
    } catch (e) {
      setError(e)
      setLoading(false)
      return undefined
    }
  }, [])

  /**
   * Delete a facility
   */
  const deleteFacility = useCallback(async (facilityId: string): Promise<void> => {
    await Api.deleteFacility(facilityId)
  }, [])

  return {
    isLoading,
    error,
    updateFacilityInfo,
    createNewFacility,
    updateFacility,
    getFacilities,
    deleteFacility,
    facilitiesList,
    totalCount,
    resetError,
  }
}
